import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useNavigate } from 'react-router-dom';

const PaymentContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding-top: 100px;
  padding-bottom: 60px;
`;

const PaymentContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
`;

const PaymentHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const PaymentTitle = styled.h1`
  font-size: 36px;
  color: #333;
  margin-bottom: 10px;
`;

const PaymentSubtitle = styled.p`
  color: #666;
  font-size: 18px;
`;

const PaymentCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
`;

const OrderSummary = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 30px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  
  &:last-child {
    margin-bottom: 0;
    border-top: 1px solid #ddd;
    padding-top: 10px;
    font-weight: 600;
    font-size: 18px;
  }
`;

const SummaryLabel = styled.span`
  color: #666;
`;

const SummaryValue = styled.span`
  color: #333;
`;

const TestPaymentCard = styled.div`
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
  color: white;
  border-radius: 12px;
  padding: 30px;
  text-align: center;
  margin-bottom: 30px;
`;

const TestTitle = styled.h2`
  font-size: 24px;
  margin-bottom: 15px;
`;

const TestDescription = styled.p`
  font-size: 16px;
  opacity: 0.9;
  margin-bottom: 20px;
`;

const PayButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 15px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const BackButton = styled.button`
  width: 100%;
  background: transparent;
  color: #ff6b35;
  border: 2px solid #ff6b35;
  border-radius: 8px;
  padding: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 20px;
  
  &:hover {
    background: #ff6b35;
    color: white;
  }
`;

const SuccessMessage = styled.div`
  background: #e8f5e8;
  border: 1px solid #4CAF50;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  margin-bottom: 20px;
`;

const SuccessTitle = styled.h3`
  color: #2e7d32;
  margin-bottom: 10px;
`;

const SuccessText = styled.p`
  color: #2e7d32;
  margin-bottom: 15px;
`;

const OrderNumber = styled.div`
  background: white;
  border: 1px solid #4CAF50;
  border-radius: 4px;
  padding: 10px;
  font-family: monospace;
  font-weight: 600;
  color: #2e7d32;
  margin-bottom: 15px;
`;

const PaymentPage = () => {
  const { isAuthenticated } = useAuth();
  const { clearCart } = useCart();
  const navigate = useNavigate();
  
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentSuccess] = useState(false);

  useEffect(() => {
    // Проверяем авторизацию
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    // Загружаем данные заказа
    const savedOrderData = localStorage.getItem('orderData');
    if (!savedOrderData) {
      navigate('/cart');
      return;
    }

    try {
      const data = JSON.parse(savedOrderData);
      setOrderData(data);
    } catch (error) {
      console.error('Ошибка загрузки данных заказа:', error);
      navigate('/cart');
    }
  }, [isAuthenticated, navigate]);

  const handlePayment = async () => {
    setLoading(true);

    try {
      // Инициализируем платеж через Robokassa
      const token = localStorage.getItem('token');
      const response = await fetch('/api/orders/robokassa/init', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: orderData.items,
          firstName: orderData.firstName,
          lastName: orderData.lastName,
          phone: orderData.phone,
          email: orderData.email,
          address: orderData.address,
          city: orderData.city,
          postalCode: orderData.postalCode,
          comment: orderData.comment,
          totalPrice: orderData.totalPrice,
          pointsToSpend: orderData.pointsToSpend,
          finalPrice: orderData.finalPrice,
          deliveryMethod: orderData.deliveryMethod,
          deliveryCost: orderData.deliveryCost,
          expertRef: orderData.expertRef || null,
          // Данные 5Post ПВЗ
          fivepostPvzCode: orderData.fivepostPvzCode || null,
          fivepostPvzAddress: orderData.fivepostPvzAddress || null,
          fivepostPvzName: orderData.fivepostPvzName || null,
          // Данные СДЭК
          cdekPvzCode: orderData.cdekPvzCode || null,
          cdekPvzAddress: orderData.cdekPvzAddress || null,
          cdekDeliveryType: orderData.cdekDeliveryType || null,
          cdekTariffCode: orderData.cdekTariffCode || null
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Ошибка при инициализации платежа');
      }

      const result = await response.json();
      
      if (!result.success || !result.data || !result.data.paymentUrl) {
        throw new Error('Не получен URL для оплаты');
      }

      console.log('Платеж инициализирован:', result.data);
      
      // Сохраняем информацию о заказе для страницы успеха
      localStorage.setItem('pendingOrder', JSON.stringify({
        orderId: result.data.orderId,
        orderNumber: result.data.orderNumber,
        amount: result.data.amount
      }));
      
      // Очищаем данные заказа и корзины перед редиректом
      localStorage.removeItem('orderData');
      localStorage.removeItem('checkoutData');
      
      // Очищаем корзину через контекст
      clearCart();
      
      // Редирект на форму оплаты Robokassa
      const paymentUrl = result.data.paymentUrl;
      const usePostForm = result.data.usePostForm || false;
      const formParams = result.data.formParams;
      
      // Если URL слишком длинный, используем POST форму, иначе GET редирект
      if (usePostForm && formParams) {
        // Используем POST форму с параметрами от бэкенда
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = paymentUrl;
        form.style.display = 'none';
        form.setAttribute('accept-charset', 'UTF-8');
        
        Object.entries(formParams).forEach(([key, value]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = value;
          form.appendChild(input);
        });
        
        document.body.appendChild(form);
        form.submit();
      } else {
        // Используем GET редирект для коротких URL
        window.location.href = paymentUrl;
      }
      
    } catch (error) {
      console.error('Ошибка оплаты:', error);
      alert(`Произошла ошибка при оплате: ${error.message}. Попробуйте еще раз.`);
      setLoading(false);
    }
  };

  const handleBackToCheckout = () => {
    navigate('/checkout');
  };

  const handleGoHome = () => {
    navigate('/');
  };

  if (!orderData) {
    return (
      <PaymentContainer>
        <PaymentContent>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <h2>Загрузка данных...</h2>
          </div>
        </PaymentContent>
      </PaymentContainer>
    );
  }

  if (paymentSuccess) {
    return (
      <PaymentContainer>
        <PaymentContent>
          <PaymentHeader>
            <PaymentTitle>Оплата успешно завершена!</PaymentTitle>
            <PaymentSubtitle>Спасибо за покупку</PaymentSubtitle>
          </PaymentHeader>

          <SuccessMessage>
            <SuccessTitle>🎉 Заказ успешно оплачен!</SuccessTitle>
            <SuccessText>
              Ваш заказ принят в обработку. Мы свяжемся с вами для подтверждения деталей доставки.
            </SuccessText>
            <OrderNumber>
              Номер заказа: {orderData.orderNumber}
            </OrderNumber>
            <div style={{ fontSize: '14px', opacity: 0.8 }}>
              На ваш email будет отправлено подтверждение заказа
            </div>
          </SuccessMessage>

          <PayButton onClick={handleGoHome}>
            Вернуться на главную
          </PayButton>
        </PaymentContent>
      </PaymentContainer>
    );
  }

  return (
    <PaymentContainer>
      <PaymentContent>
        <PaymentHeader>
          <PaymentTitle>Оплата заказа</PaymentTitle>
          <PaymentSubtitle>Выберите способ оплаты</PaymentSubtitle>
        </PaymentHeader>

        <OrderSummary>
          <SummaryRow>
            <SummaryLabel>Номер заказа</SummaryLabel>
            <SummaryValue>{orderData.orderNumber}</SummaryValue>
          </SummaryRow>
          <SummaryRow>
            <SummaryLabel>Товары ({orderData.items.length})</SummaryLabel>
            <SummaryValue>{orderData.totalPrice.toLocaleString()} ₽</SummaryValue>
          </SummaryRow>
          {orderData.discountAmount > 0 && (
            <SummaryRow>
              <SummaryLabel>Скидка</SummaryLabel>
              <SummaryValue style={{ color: '#2e7d32' }}>-{orderData.discountAmount.toLocaleString()} ₽</SummaryValue>
            </SummaryRow>
          )}
          {orderData.pointsToSpend > 0 && (
            <SummaryRow>
              <SummaryLabel>Списано баллов</SummaryLabel>
              <SummaryValue style={{ color: '#ff6b35' }}>-{orderData.pointsToSpend}</SummaryValue>
            </SummaryRow>
          )}
          <SummaryRow>
            <SummaryLabel>Доставка</SummaryLabel>
            <SummaryValue style={{ color: orderData.deliveryCost === 0 ? '#28a745' : '#333' }}>
              {orderData.deliveryCost === 0 ? 'Бесплатно' : `${orderData.deliveryCost.toLocaleString()} ₽`}
            </SummaryValue>
          </SummaryRow>
          <SummaryRow>
            <SummaryLabel>К доплате</SummaryLabel>
            <SummaryValue style={{ color: '#2e7d32', fontWeight: '600' }}>
              {orderData.finalPrice.toLocaleString()} ₽
            </SummaryValue>
          </SummaryRow>
        </OrderSummary>

        <PaymentCard>
          <TestPaymentCard>
            <TestTitle>💳 Оплата через Robokassa</TestTitle>
            <TestDescription>
              После нажатия кнопки "Оплатить" вы будете перенаправлены на безопасную страницу оплаты Robokassa, где вы сможете выбрать способ оплаты: банковскую карту или СБП (Система быстрых платежей).
            </TestDescription>
          </TestPaymentCard>

          <BackButton type="button" onClick={handleBackToCheckout}>
            Вернуться к оформлению
          </BackButton>

          <PayButton onClick={handlePayment} disabled={loading}>
            {loading ? 'Обрабатываем оплату...' : 'Оплатить'}
          </PayButton>
        </PaymentCard>
      </PaymentContent>
    </PaymentContainer>
  );
};

export default PaymentPage;
